import { getViewData } from './view-data';
import moment from './moment';

class MapUtilities {

    /**
     * Statically obtain one of the methods below
     *
     * @param key
     * @param args
     * @return {*}
     */
    get(key, ...args) {
        const method = this[key];
        if (method) {
            return method(...args);
        }
        return null;
    }

    /**
     * Return the path to an icon
     *
     * @param {String} icon
     *
     * @return {String}
     */
    getMarkerIconPath(icon) {
        if (getViewData('microsite.id')) {
            return `/images/frontend/map-markers/partners/${getViewData('microsite.name')}/${icon}`;
        }

        return `/images/frontend/map-markers/${icon}`;
    }

    /**
     * Create a marker for the "current location" pin after geolocating
     *
     * @returns {HTMLElement}
     */
    createGeolocationMarker() {
        const markerContainer = document.createElement('div');

        const marker = document.createElement('div');
        marker.className = 'geolocation-marker';

        markerContainer.appendChild(marker);

        return markerContainer;
    }

    /**
     * Create the marker pin elements, and put the custom content inside
     *
     * @param {HTMLElement} content
     *
     * @returns {HTMLElement}
     */
    createMarkerPin(content) {
        const container = document.createElement('div');
        container.className = 'marker-container';

        const marker = document.createElement('div');
        marker.className = 'marker';

        const markerSign = document.createElement('div');
        markerSign.className = 'marker-sign';

        const markerPost = document.createElement('div');
        markerPost.className = 'marker-post';

        markerSign.appendChild(content);

        marker.appendChild(markerSign);
        marker.appendChild(markerPost);

        container.appendChild(marker);

        return container;
    }

    /**
     * Logic for whether the pin is displayed as available soon
     *
     * @param {Object} space
     *
     * @returns {*|null|boolean}
     */
    spaceIsAvailableSoon(space) {
        return space.available_from
            && moment(space.available_from).isSameOrAfter(moment())
            && space.enabled;
    }

    /**
     * Create a marker element
     *
     * @param {Object} space
     *
     * @returns {HTMLElement}
     */
    createMarker(space) {
        const options = {
            label: 'BOOK NOW',
            classList: ['marker-default'],
        };

        const content = document.createElement('div');
        content.className = 'marker-content';

        if (space.waiting_list) {
            options.classList = ['marker-let'];
            options.label = 'SOLD OUT';
            options.value = '';
        } else if (this.spaceIsAvailableSoon(space)) {
            options.classList.push('marker-available-soon');
            options.label = 'BOOK FROM';
            options.value = moment(space.available_from).format('Do MMM');
        } else if (space.airport_space) {
            options.value = '';
            options.classList.push('airport-marker');
        } else {
            options.value = `£${ this.pinLookupData(space) }`;
        }

        ['label', 'value'].forEach((item) => {
            if (options[item]) {
                const p = document.createElement('p');
                p.innerText = options[item];

                content.appendChild(p);
            }
        });

        const marker = this.createMarkerPin(content);

        marker.classList.add(...options.classList);
        marker.setAttribute('data-space', JSON.stringify(space));

        return marker;
    }

    /**
     * Return the data used in the pin lookup request
     *
     * @param {Object} space
     *
     * @return {Number|String}
     */
    pinLookupData(space) {
        if (this.spaceIsAvailableSoon(space)) {
            return encodeURIComponent(space.available_from);
        }

        const value = space.quote && space.quote.value || 0;

        return value.toFixed(2);
    }

    /**
     * Icon definition for the center marker
     *
     * @return {HTMLElement}
     */
    centerIcon() {
        const marker = document.createElement('div');

        marker.className = 'center-icon';
        marker.innerHTML = `
            <svg
                viewBox="0 0 19 26"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
            >
                <path d="M1.90625,0.96875 C1.863281,0.9765625 1.820313,0.9882812 1.78125,1 C1.445313,1.078125 1.171875,1.328125 1.0625,1.65625 L1,1.625 L1,1.8125 C0.992188,1.875 0.992188,1.9375 1,2 L1,26 C0.996094,26.359375 1.183594,26.695313 1.496094,26.878906 C1.808594,27.058594 2.191406,27.058594 2.503906,26.878906 C2.816406,26.695313 3.003906,26.359375 3,26 L3,17.46875 L19.375,10 L3,2.53125 L3,2 C3.011719,1.710938 2.894531,1.433594 2.6875,1.238281 C2.476563,1.0390625 2.191406,0.9414062 1.90625,0.96875 Z"></path>
            </svg>
        `;

        return marker;
    }

    /**
     * Icon definition for a single location marker
     *
     * @return {{url: string, scaledSize: global.google.maps.Size, size: global.google.maps.Size, origin: global.google.maps.Point, anchor: global.google.maps.Point}}
     */
    selectedSpaceIcon() {
        return {
            url: '/images/pin.png',
            scaledSize: new global.google.maps.Size(121, 37),
            size: new global.google.maps.Size(242, 74),
            origin: new global.google.maps.Point(0, 0),
            anchor: new global.google.maps.Point(60, 37),
        };
    }

    /**
     * Icon definition for a single listing marker
     *
     * @return {HTMLElement}
     */
    selectedSpaceMap() {
        const marker = document.createElement('img');
        marker.src = '/images/frontend/listing-pin.png';
        marker.style.width = '195px';
        marker.style.height = '72px';

        return marker;
    }

    /**
     * Icon definition at default sizing
     *
     * @param {String|Boolean} icon
     *
     * @return {HTMLElement}
     */
    defaultIcon(icon = false) {
        const marker = document.createElement('img');
        marker.src = icon;
        marker.style.width = '81px';
        marker.style.height = 'auto';

        return marker;
    }

    /**
     * Icon definition for a cluster
     *
     * @return {{url: string, scaledSize: google.maps.Size, origin: google.maps.Point, anchor: google.maps.Point}}
     */
    clusterIcon() {
        let icon = '/images/frontend/map-markers/let.png';

        return {
            url: icon,
            scaledSize: new google.maps.Size(89, 68),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(45, 68),
        };
    }

    /**
     * Marker cluster label definition
     *
     * @param markers
     * @return {{text: string, fontSize: string, fontWeight: string, fontFamily: string, color: string}}
     */
    clusterLabel(markers) {
        return {
            text: `${ markers.length } spaces`,
            fontSize: '12px',
            fontWeight: '400',
            fontFamily: 'Mont',
            color: '#ffffff',
        };
    }

    /**
     * Icon definition for a "let" marker
     *
     * @param {String|Boolean} icon
     *
     * @returns {HTMLElement}
     */
    letIcon(icon = null) {
        if (!icon && getViewData('microsite')) {
            icon = getViewData('microsite.map_markers.let');
        }

        const marker = document.createElement('img');
        marker.src = icon;
        marker.style.width = '50px';
        marker.style.height = 'auto';

        return marker;
    }

    /**
     * Default marker definition for general usage
     *
     * @return {{url: string, scaledSize: global.google.maps.Size, size: global.google.maps.Size, origin: global.google.maps.Point, anchor: global.google.maps.Point}}
     */
    partnerIcon() {
        const marker = document.createElement('img');
        marker.src = '/images/frontend/parking-icon.png';
        marker.style.width = '70px';
        marker.style.height = '79px';

        return marker;
    }

    /**
     * Icon definition for a single listing marker
     *
     * @return {{url: string, scaledSize: global.google.maps.Size, size: global.google.maps.Size, origin: global.google.maps.Point, anchor: global.google.maps.Point}}
     */
    listingIcon() {
        const marker = document.createElement('img');
        marker.src = '/images/frontend/listing-pin.png';
        marker.style.width = '195px';
        marker.style.height = '72px';

        return marker;
    }
}

export default new MapUtilities;
